import { useContext, useState, useCallback, type FC } from 'react';
import {
  Button,
  Dialog,
  Alert,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/UploadFile';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import { AppContext } from '../../App';
import useGlynt from '../hooks/useGlynt';
import type { ImportResult, ImportButtonProps } from './ImportButton.types';
import {
  validateFile,
  createFileInput,
  formatImportSuccessMessage,
  parseRowError,
} from './ImportButton.utils';
import { btnSubtlePrimarySx } from '../common/filterStyles';

const IMPORT_BUTTON_TEXT = 'Import';

const ImportButton: FC<ImportButtonProps> = ({ onImported }) => {
  const { isStaff, setToast, orgId } = useContext(AppContext);
  const glynt = useGlynt();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const onSelectFile = useCallback(
    (f: File) => {
      const validation = validateFile(f);

      if (!validation.isValid) {
        if (validation.errorMessage) {
          setToast?.({ severity: 'error', message: validation.errorMessage });
        }
        return;
      }

      setFile(f);
    },
    [setToast]
  );

  const submit = useCallback(async () => {
    if (!file || !orgId) return;

    setUploading(true);
    try {
      const g = await glynt;
      const res: ImportResult = await g.mat.importCsv(orgId, file);
      setResult(res);

      const successMessage = formatImportSuccessMessage(res);
      setToast?.({ severity: 'success', message: successMessage });
      onImported?.();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Import failed';
      setToast?.({ severity: 'error', message: errorMessage });
    } finally {
      setUploading(false);
    }
  }, [file, orgId, glynt, setToast, onImported]);

  if (!isStaff) return null;

  return (
    <>
      <Button
        onClick={() => {
          setFile(null);
          setResult(null);
          setOpen(true);
        }}
        data-cy="btn-import-mat"
        disabled={uploading}
        variant="outlined"
        sx={btnSubtlePrimarySx}
        startIcon={
          uploading ? <CircularProgress size={14} /> : <UploadIcon sx={{ fontSize: 16 }} />
        }
      >
        {IMPORT_BUTTON_TEXT}
      </Button>
      <Dialog open={open} onClose={() => !uploading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitleGlynt
          title="Import MAT Entries"
          closeModal={() => setOpen(false)}
          loading={uploading}
        />
        <DialogContentGlynt>
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files?.[0]) onSelectFile(e.dataTransfer.files[0]);
            }}
            sx={{
              border: '2px dashed #999',
              borderRadius: 1,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 2,
            }}
            onClick={() => createFileInput(onSelectFile)}
          >
            <Typography variant="body2" color="textSecondary">
              {file ? file.name : 'Drop CSV here or click to select'}
            </Typography>
          </Box>
          {result && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Processed {result.processed} (created {result.created} / updated {result.updated})
            </Alert>
          )}
          {result?.errors && result.errors.length > 0 && (
            <>
              <Alert severity="warning" sx={{ mb: 1 }}>
                {result.errors.length} row errors.
              </Alert>
              <Box
                sx={{
                  maxHeight: 260,
                  overflow: 'auto',
                  mt: 1,
                  border: '1px solid #eee',
                }}
              >
                <Table size="small" stickyHeader aria-label="Import row errors table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 80 }}>Row</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((error, i) => {
                      const { message } = parseRowError(error);

                      return (
                        <TableRow key={i} hover>
                          <TableCell>{error.row}</TableCell>
                          <TableCell style={{ whiteSpace: 'pre-wrap' }}>{message}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContentGlynt>
        <DialogActionsGlynt
          cancelCypressId="import-dialog-cancel"
          submitCypressId="import-dialog-submit"
          clearCypressId="noop"
          handleSubmit={submit}
          closeModal={() => {
            setOpen(false);
            setTimeout(() => {
              setFile(null);
              setResult(null);
            }, 300);
          }}
          showClearButton={false}
          submitButtonText={uploading ? 'Importing…' : IMPORT_BUTTON_TEXT}
          submitLoading={uploading}
          submitDisabled={!file || uploading}
        />
      </Dialog>
    </>
  );
};

export default ImportButton;
